<script setup lang="ts">
/**
 * The count sticker on the upper trailing corner of an icon button
 * (`NxIconRail` items, `NxIconButton` with `badge`). It is positioned against
 * the nearest positioned ancestor — the button — and is decoration: the
 * button carries the count in its accessible name, so this is `aria-hidden`.
 */
import { computed } from 'vue';
import { countBadgeText, type NxCountBadgeTone, type NxCountBadgeValue } from './countBadge';

const props = withDefaults(
  defineProps<{
    badge: NxCountBadgeValue;
    tone?: NxCountBadgeTone;
    /** Only a dot: something is there, however much. */
    dot?: boolean;
  }>(),
  { tone: 'brand', dot: false }
);

const text = computed(() => (props.dot ? '' : countBadgeText(props.badge)));
</script>

<template>
  <span
    class="nx-count-badge"
    :class="[`tone-${props.tone}`, { 'is-dot': props.dot }]"
    aria-hidden="true"
  >
    {{ text }}
  </span>
</template>

<style scoped>
/* On the upper trailing corner, ringed in the surface so it reads as a sticker
   on the button rather than part of the icon. */
.nx-count-badge {
  /* The corner, as variables so a host that positions its children itself can
     point the badge back at it (NxIconButton). */
  --count-badge-top: calc(var(--nxt-space-2xs) * -1);
  --count-badge-end: calc(var(--nxt-space-2xs) * -1.5);

  position: absolute;
  inset-block-start: var(--count-badge-top);
  inset-inline-end: var(--count-badge-end);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: calc(var(--nxt-size-unit) * 1);
  height: calc(var(--nxt-size-unit) * 1);
  padding: 0 var(--nxt-space-2xs);
  border-radius: var(--nxt-radius-full);
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-surface-bg);
  font-size: calc(var(--nxt-font-size-xs) * 0.85);
  font-weight: var(--nxt-font-weight-bold);
  font-variant-numeric: tabular-nums;
  line-height: 1;
  pointer-events: none;
}
.nx-count-badge.is-dot {
  min-width: calc(var(--nxt-size-unit) * 0.5);
  height: calc(var(--nxt-size-unit) * 0.5);
  padding: 0;
}
.nx-count-badge.tone-brand {
  background: var(--nxt-brand-bg);
  color: var(--nxt-brand-fg);
}
/* Reversed muted ink: a sunken fill vanished against the surface. */
.nx-count-badge.tone-neutral {
  background: var(--nxt-fg-muted);
  color: var(--nxt-surface-bg);
}
.nx-count-badge.tone-warning {
  background: var(--nxt-warning-bg);
  color: var(--nxt-warning-fg);
}
.nx-count-badge.tone-danger {
  background: var(--nxt-danger-bg);
  color: var(--nxt-danger-fg);
}
</style>
